<script lang="ts">
  import { brandStore } from '@/lib/brandStore.svelte';
  import { session } from '@/lib/session.svelte';
  import { navigateTo } from '@/lib/router';
  import { classify, type InferenceResult } from '@/lib/onnxInference';

  type VideoFrame = {
    frameIndex: number;
    time: number;
    className: string | null;
    confidence: number;
    allScores: Partial<Record<string, number>>;
    thumbnailDataUrl: string;
  };

  let phase = $state<'idle' | 'preview' | 'processing' | 'results'>('idle');
  let videoUrl = $state<string | null>(null);
  let videoDuration = $state(0);
  let errorMsg = $state('');

  let framesExtracted = $state(0);
  let currentFrameIdx = $state(0);
  let progressPct = $state(0);
  let cancelled = $state(false);
  let frameResults = $state<VideoFrame[]>([]);

  let videoInput: HTMLInputElement;
  let videoEl: HTMLVideoElement;
  let abortController = $state<AbortController | null>(null);

  let partsFound = $derived(
    frameResults.filter((d) => d.className && d.confidence > 0.35).length
  );
  let isExtracting = $state(true);
  let framesAnalyzed = $derived(currentFrameIdx);
  let progress = $derived(progressPct);

  function handleVideoSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    videoUrl = URL.createObjectURL(file);
    session.setVideoFile(file);
    phase = 'preview';
    errorMsg = '';
    framesExtracted = 0;
    currentFrameIdx = 0;
    progressPct = 0;
    cancelled = false;
    frameResults = [];
  }

  function clearVideo() {
    session.clearVideo();
    videoUrl = null;
    phase = 'idle';
    framesExtracted = 0;
    currentFrameIdx = 0;
    progressPct = 0;
    cancelled = false;
    frameResults = [];
    videoInput.value = '';
  }

  function scoreColor(conf: number): string {
    if (conf >= 0.7) return 'high';
    if (conf >= 0.4) return 'medium';
    return 'low';
  }

  async function processVideo() {
    if (!videoEl || !videoDuration) return;
    cancelled = false;
    framesExtracted = 0;
    currentFrameIdx = 0;
    progressPct = 0;
    frameResults = [];
    isExtracting = true;
    session.setVideoDuration(videoDuration);
    const totalFrames = Math.ceil(videoDuration);

    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth || 640;
    canvas.height = videoEl.videoHeight || 480;
    const ctx = canvas.getContext('2d')!;

    const abortCtrl = new AbortController();
    abortController = abortCtrl;

    for (let t = 0; t <= videoDuration; t++) {
      if (cancelled || abortCtrl.signal.aborted) break;

      const frameResult = await new Promise<InferenceResult | null>((resolve) => {
        let settled = false;
        const cleanup = () => {
          settled = true;
          videoEl.removeEventListener('seeked', onSeeked);
        };
        const onSeeked = () => {
          cleanup();
          ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          framesExtracted = t + 1;
          currentFrameIdx = t;
          progressPct = Math.round((framesExtracted / totalFrames) * 100);
          isExtracting = false;
          classify(imgData).then((result: InferenceResult) => {
            if (!cancelled) resolve(result);
            else resolve(null);
          }).catch(() => resolve(null));
        };
        videoEl.addEventListener('seeked', onSeeked);
        videoEl.currentTime = t;
        setTimeout(() => {
          if (!settled) {
            cleanup();
            ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            framesExtracted = t + 1;
            currentFrameIdx = t;
            progressPct = Math.round((framesExtracted / totalFrames) * 100);
            isExtracting = false;
            classify(imgData).then((result: InferenceResult) => {
              if (!cancelled) resolve(result);
              else resolve(null);
            }).catch(() => resolve(null));
          }
        }, 2000);
      });

      if (cancelled || abortCtrl.signal.aborted) break;
      if (frameResult) {
        const thumbnail = document.createElement('canvas');
        thumbnail.width = 80;
        thumbnail.height = 60;
        thumbnail.getContext('2d')!.drawImage(canvas, 0, 0, 80, 60);
        frameResults = [...frameResults, {
          frameIndex: t,
          time: t,
          className: frameResult.className,
          confidence: frameResult.confidence,
          allScores: frameResult.allScores,
          thumbnailDataUrl: thumbnail.toDataURL('image/jpeg', 0.6),
        }];
      }
    }

    if (!cancelled) {
      session.setVideoDetections(frameResults);
      phase = 'results';
    }
  }

  function cancelProcessing() {
    cancelled = true;
    abortController?.abort();
    abortController = null;
    session.clearVideo();
    videoUrl = null;
    phase = 'idle';
    frameResults = [];
    isExtracting = true;
  }

  function goBack() {
    session.clearVideo();
    videoUrl = null;
    frameResults = [];
    navigateTo('upload');
  }

  function goToResults() {
    navigateTo('videoresults');
  }
</script>

<div id="video-analysis-screen" class="screen">
  <div class="screen-header">
    <button class="btn-back" type="button" onclick={goBack} disabled={phase === 'processing'}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    </button>
    <h2 class="screen-title">Análise de Vídeo</h2>
  </div>

  {#if phase === 'idle' || phase === 'preview'}
    {#if !videoUrl}
      <div class="upload-dropzone">
        <div class="upload-dropzone-icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="23 7 16 12 23 17 23 7"/>
            <rect x="1" y="5" width="15" height="14" rx="2"/>
          </svg>
        </div>
        <p class="upload-dropzone-text">Selecione um vídeo do seu dispositivo</p>
        <button class="btn-upload" type="button" onclick={() => videoInput.click()}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span>Selecionar Vídeo</span>
        </button>
      </div>
    {:else}
      <div class="video-preview-wrap">
        <video
          bind:this={videoEl}
          src={videoUrl}
          controls
          onloadedmetadata={(e) => { videoDuration = (e.target as HTMLVideoElement).duration; }}
          class="video-preview-player"
        ></video>
        <button class="preview-clear" type="button" onclick={clearVideo} aria-label="Remover vídeo">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    {/if}

    <input type="file" bind:this={videoInput} accept="video/*" onchange={handleVideoSelect} style="display:none" />

    <button
      class="btn btn-process"
      type="button"
      disabled={phase !== 'preview' || !videoDuration}
      onclick={processVideo}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
      Processar Vídeo
    </button>
  {/if}

  {#if phase === 'processing'}
    <div class="processing-container">
      <video
        bind:this={videoEl}
        src={videoUrl}
        muted
        class="processing-preview"
      ></video>

      <div class="processing-info">
        <div class="progress-bar-container">
          <div class="progress-bar-fill" style="width: {progress}%"></div>
        </div>

        <div class="processing-stats">
          <div class="stat-item">
            <span class="stat-value">{framesExtracted}</span>
            <span class="stat-label">frames</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{framesAnalyzed}</span>
            <span class="stat-label">analisados</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{partsFound}</span>
            <span class="stat-label">peças</span>
          </div>
          <div class="stat-item">
            <span class="stat-value">{Math.round(progress)}%</span>
            <span class="stat-label">progresso</span>
          </div>
        </div>

        <div class="activity-indicator">
          {#if isExtracting}
            <span class="activity-dot extracting"></span>
            <span>Extraindo frames...</span>
          {:else}
            <span class="activity-dot analyzing"></span>
            <span>Analisando com IA...</span>
          {/if}
        </div>
      </div>

      <button class="btn btn-outline" type="button" onclick={cancelProcessing}>
        Cancelar
      </button>
    </div>
  {/if}

  {#if phase === 'results' && frameResults.length > 0}
    <div class="results-header">
      <div class="results-summary">
        <span class="results-count">{frameResults.length}</span>
        <span class="results-count-label">quadros analisados</span>
      </div>
    </div>

    <div class="frames-grid">
      {#each frameResults as frame}
        <div class="frame-card">
          <div class="frame-time">{(frame.time || 0).toFixed(1)}s</div>
          <div class="frame-category {scoreColor(frame.confidence)}">
            {brandStore.translateCategory(frame.className ?? 'outro')}
          </div>
          <div class="frame-confidence">
            <div class="conf-bar">
              <div class="conf-fill {scoreColor(frame.confidence)}" style="width: {frame.confidence * 100}%"></div>
            </div>
            <span class="conf-pct">{(frame.confidence * 100).toFixed(0)}%</span>
          </div>
        </div>
      {/each}
    </div>

    <button class="btn btn-process" type="button" onclick={goToResults}>
      Ver Detalhes Completos
    </button>
  {/if}

  {#if errorMsg}
    <div class="error-card visible" role="alert">
      <svg class="error-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <span>{errorMsg}</span>
    </div>
  {/if}
</div>

<style>
  .screen-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  .btn-back {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #cbd5e1;
    transition: background 0.2s;
    flex-shrink: 0;
  }

  .btn-back:hover:not(:disabled) { background: rgba(255,255,255,0.12); }
  .btn-back:disabled { opacity: 0.4; cursor: not-allowed; }

  .screen-title {
    font-size: 18px;
    font-weight: 700;
    color: white;
    margin: 0;
  }

  .upload-dropzone {
    border: 2px dashed rgba(255,255,255,0.15);
    border-radius: 16px;
    padding: 48px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    background: rgba(255,255,255,0.03);
    margin-bottom: 16px;
  }

  .upload-dropzone-icon {
    color: rgba(255,255,255,0.3);
  }

  .upload-dropzone-text {
    font-size: 15px;
    color: rgba(255,255,255,0.6);
    margin: 0;
    text-align: center;
  }

  .btn-upload {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(59,130,246,0.15);
    border: 1px solid rgba(59,130,246,0.4);
    color: #60a5fa;
    font-weight: 600;
    font-size: 14px;
    border-radius: 10px;
    padding: 10px 20px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-outline {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: transparent;
    color: rgba(255,255,255,0.5);
    font-weight: 600;
    font-size: 14px;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 10px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-outline:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.3);
    color: rgba(255,255,255,0.8);
  }

  .btn-upload:hover { background: rgba(59,130,246,0.25); border-color: rgba(59,130,246,0.6); }

  .video-preview-wrap {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
    background: rgba(0,0,0,0.3);
  }

  .video-preview-player {
    width: 100%;
    max-height: 320px;
    display: block;
    border-radius: 12px;
  }

  .preview-clear {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0,0,0,0.7);
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: background 0.2s;
  }

  .preview-clear:hover { background: rgba(239,68,68,0.8); }

  .btn-process {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #3b82f6;
    color: white;
    font-weight: 700;
    font-size: 15px;
    border: none;
    border-radius: 12px;
    padding: 14px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
    margin-bottom: 16px;
  }

  .btn-process:hover:not(:disabled) { background: #2563eb; }
  .btn-process:active:not(:disabled) { transform: scale(0.98); }
  .btn-process:disabled { opacity: 0.45; cursor: not-allowed; }

  .processing-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .processing-preview {
    width: 100%;
    border-radius: 12px;
    max-height: 220px;
    object-fit: cover;
    background: rgba(0,0,0,0.4);
  }

  .processing-info {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 20px;
    background: rgba(255,255,255,0.04);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 16px;
  }

  .progress-bar-container {
    width: 100%;
    height: 6px;
    background: rgba(255,255,255,0.08);
    border-radius: 3px;
    overflow: hidden;
  }

  .progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #7c6ef5, #22d3ee);
    border-radius: 3px;
    transition: width 0.25s ease;
  }

  .processing-stats {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 800;
    color: #7c6ef5;
    line-height: 1;
  }

  .stat-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255,255,255,0.4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 4px;
  }

  .activity-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: rgba(255,255,255,0.6);
  }

  .activity-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .activity-dot.extracting {
    background: #f59e0b;
    animation: pulse-dot 1.2s ease-in-out infinite;
  }

  .activity-dot.analyzing {
    background: #22d3ee;
    animation: pulse-dot 1.2s ease-in-out infinite;
  }

  @keyframes pulse-dot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.4; transform: scale(0.75); }
  }

  .results-header {
    margin-bottom: 16px;
  }

  .results-summary {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .results-count {
    font-size: 32px;
    font-weight: 800;
    color: #3b82f6;
  }

  .results-count-label {
    font-size: 15px;
    color: rgba(255,255,255,0.5);
  }

  .frames-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 10px;
    margin-bottom: 16px;
  }

  .frame-card {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .frame-time {
    font-size: 11px;
    color: rgba(255,255,255,0.4);
    font-weight: 600;
  }

  .frame-category {
    font-size: 13px;
    font-weight: 700;
    color: white;
  }

  .frame-category.high { color: #86efac; }
  .frame-category.medium { color: #fcd34d; }
  .frame-category.low { color: #f87171; }

  .frame-confidence {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .conf-bar {
    flex: 1;
    height: 4px;
    background: rgba(255,255,255,0.1);
    border-radius: 2px;
    overflow: hidden;
  }

  .conf-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.3s ease;
  }

  .conf-fill.high { background: #22c55e; }
  .conf-fill.medium { background: #f59e0b; }
  .conf-fill.low { background: #ef4444; }

  .conf-pct {
    font-size: 11px;
    color: rgba(255,255,255,0.4);
    font-weight: 600;
    min-width: 32px;
    text-align: right;
  }

  .error-card {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.3);
    border-radius: 10px;
    padding: 12px 14px;
    color: #f87171;
    font-size: 14px;
    margin-top: 8px;
  }

  .error-card.visible { display: flex; }
  .error-card { display: none; }
</style>
